import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Divider
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Hardware as HardwareIcon
} from '@mui/icons-material';

const TaskSelectionDialog = ({ 
  open, 
  onClose, 
  originalTasks,      // { software: [], hardware: [] } 原始完整列表
  currentSelection,   // { software: [], hardware: [] } 用户当前选择
  isRestore, 
  updateId, 
  onConfirm 
}) => {
  const { t } = useTranslation();
  const [selectedSoftware, setSelectedSoftware] = useState([]);
  const [selectedHardware, setSelectedHardware] = useState([]);

  // 初始化选择状态 - 重构后的清晰逻辑
  useEffect(() => {
    if (open && currentSelection) {
      console.log("TaskSelectionDialog: 初始化选择状态", currentSelection);
      // 🔑 首次打开（恢复模式为false且当前选择为空）时，默认不选中任何模块
      if (!isRestore && currentSelection.software.length === 0 && currentSelection.hardware.length === 0) {
        setSelectedSoftware([]);
        setSelectedHardware([]);
      } else {
        // 恢复模式或已有选择时，使用当前选择
        setSelectedSoftware([...currentSelection.software]);
        setSelectedHardware([...currentSelection.hardware]);
      }
    }
  }, [open, currentSelection, updateId, isRestore]);

  const handleSoftwareToggle = (task) => {
    setSelectedSoftware(prev => 
      prev.includes(task) 
        ? prev.filter(t => t !== task)
        : [...prev, task]
    );
  };

  const handleHardwareToggle = (task) => {
    setSelectedHardware(prev => 
      prev.includes(task) 
        ? prev.filter(t => t !== task)
        : [...prev, task]
    );
  };

  // 显示任务列表 - 重构后始终使用原始完整列表
  const displaySoftwareTasks = originalTasks?.software || [];
  const displayHardwareTasks = originalTasks?.hardware || [];
    
  // 如果没有任何可显示的任务，不渲染对话框
  if (displaySoftwareTasks.length === 0 && displayHardwareTasks.length === 0) {
    return null;
  }

  const handleSelectAllSoftware = () => {
    setSelectedSoftware(
      selectedSoftware.length === displaySoftwareTasks.length 
        ? [] 
        : [...displaySoftwareTasks]
    );
  };

  const handleSelectAllHardware = () => {
    setSelectedHardware(
      selectedHardware.length === displayHardwareTasks.length 
        ? [] 
        : [...displayHardwareTasks]
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedSoftware, selectedHardware);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {}} // 🔑 禁用点击外部关闭，防止后端等待超时
      disableEscapeKeyDown  // 🔑 禁用ESC键关闭
      maxWidth={false}
      PaperProps={{
        style: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          borderRadius: '12px',
          border: '1px solid rgba(52, 152, 219, 0.3)',
          width: '720px',
          maxWidth: '90vw'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#2c3e50', 
        color: '#ffffff',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '16px 24px'
      }}>
        <Box>
          <Typography variant="h6">
            {isRestore ? t('task_restore_title') : t('task_select_title')}
          </Typography>
          {isRestore && (
            <Typography variant="caption" sx={{ color: '#ffa726', display: 'block', mt: 0.5 }}>
              {t('task_restore_mode')}
            </Typography>
          )}
        </Box>
        {/* 🔑 移除X按钮，强制用户点击"确认选择" */}
      </DialogTitle>
      
      <DialogContent sx={{ 
        backgroundColor: '#1a1a1a', 
        padding: '20px 24px',
        maxHeight: '70vh',
        overflow: 'auto'
      }}>
        {/* 🔑 醒目提示：只选择感兴趣的模块 */}
        <Box sx={{ 
          marginBottom: '16px', 
          padding: '16px', 
          backgroundColor: 'rgba(255, 152, 0, 0.15)', 
          border: '2px solid rgba(255, 152, 0, 0.5)', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ 
            color: '#ffa726', 
            fontWeight: 600,
            marginBottom: '8px',
            fontSize: '16px'
          }}>
            {t('task_important_hint')}
          </Typography>
          <Typography variant="body2" sx={{ 
            color: '#ffcc80',
            fontSize: '14px',
            lineHeight: 1.6
          }}>
            {isRestore 
              ? t('task_restore_hint')
              : t('task_select_hint_detail')}
          </Typography>
        </Box>
        
        <Typography variant="body1" gutterBottom sx={{ 
          color: '#cccccc', 
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          {isRestore ? t('task_restore_prompt') : t('task_select_prompt')}
        </Typography>
        
        {/* 任务统计信息显示 */}
        <Box sx={{ 
          marginBottom: '16px', 
          padding: '12px', 
          backgroundColor: 'rgba(52, 152, 219, 0.1)', 
          border: '1px solid rgba(52, 152, 219, 0.3)', 
          borderRadius: '6px' 
        }}>
          <Typography variant="body2" sx={{ color: '#3498db', marginBottom: '8px', fontWeight: 500 }}>
            {t('task_statistics')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#cccccc', fontSize: '12px' }}>
            {t('task_software_count', { total: displaySoftwareTasks.length, selected: selectedSoftware.length })} | 
            {t('task_hardware_count', { total: displayHardwareTasks.length, selected: selectedHardware.length })}
          </Typography>
        </Box>
        
        {/* 横向布局的两列容器 */}
        <Box sx={{ 
          display: 'flex', 
          gap: '20px',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          {/* 软件模块部分 */}
          {displaySoftwareTasks && displaySoftwareTasks.length > 0 && (
            <Box sx={{ width: '40%', maxWidth: '300px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <ComputerIcon sx={{ color: '#7cb8e8', marginRight: '8px', fontSize: '20px' }} />
                <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                  {t('task_software_title', { selected: selectedSoftware.length, total: displaySoftwareTasks.length })}
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleSelectAllSoftware} 
                  sx={{ 
                    marginLeft: 'auto',
                    color: '#7cb8e8',
                    fontSize: '12px',
                    minWidth: 'auto',
                    padding: '4px 8px',
                    '&:hover': { backgroundColor: 'rgba(124, 184, 232, 0.08)' }
                  }}
                >
                  {selectedSoftware.length === displaySoftwareTasks.length ? t('task_deselect_all') : t('task_select_all')}
                </Button>
              </Box>
              
              <Box sx={{ 
                border: '1px solid rgba(124, 184, 232, 0.2)', 
                borderRadius: '6px',
                padding: '12px',
                backgroundColor: 'rgba(124, 184, 232, 0.03)',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(124, 184, 232, 0.3)',
                  borderRadius: '3px',
                  '&:hover': {
                    backgroundColor: 'rgba(124, 184, 232, 0.5)',
                  }
                }
              }}>
                {displaySoftwareTasks.map((task, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={selectedSoftware.includes(task)}
                        onChange={() => handleSoftwareToggle(task)}
                        size="small"
                        sx={{ 
                          color: '#7cb8e8',
                          '&.Mui-checked': { color: '#7cb8e8' },
                          padding: '4px'
                        }}
                      />
                    }
                    label={<Typography sx={{ color: '#e8e8e8', fontSize: '13px' }}>{task}</Typography>}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      marginBottom: '2px',
                      width: '100%',
                      marginLeft: 0,
                      marginRight: 0
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* 硬件模块部分 */}
          {displayHardwareTasks && displayHardwareTasks.length > 0 && (
            <Box sx={{ width: '40%', maxWidth: '300px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <HardwareIcon sx={{ color: '#e8a87c', marginRight: '8px', fontSize: '20px' }} />
                <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                  {t('task_hardware_title', { selected: selectedHardware.length, total: displayHardwareTasks.length })}
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleSelectAllHardware} 
                  sx={{ 
                    marginLeft: 'auto',
                    color: '#e8a87c',
                    fontSize: '12px',
                    minWidth: 'auto',
                    padding: '4px 8px',
                    '&:hover': { backgroundColor: 'rgba(232, 168, 124, 0.08)' }
                  }}
                >
                  {selectedHardware.length === displayHardwareTasks.length ? t('task_deselect_all') : t('task_select_all')}
                </Button>
              </Box>
              
              <Box sx={{ 
                border: '1px solid rgba(232, 168, 124, 0.2)', 
                borderRadius: '6px',
                padding: '12px',
                backgroundColor: 'rgba(232, 168, 124, 0.03)',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(232, 168, 124, 0.3)',
                  borderRadius: '3px',
                  '&:hover': {
                    backgroundColor: 'rgba(232, 168, 124, 0.5)',
                  }
                }
              }}>
                {displayHardwareTasks.map((task, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={selectedHardware.includes(task)}
                        onChange={() => handleHardwareToggle(task)}
                        size="small"
                        sx={{ 
                          color: '#e8a87c',
                          '&.Mui-checked': { color: '#e8a87c' },
                          padding: '4px'
                        }}
                      />
                    }
                    label={<Typography sx={{ color: '#e8e8e8', fontSize: '13px' }}>{task}</Typography>}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      marginBottom: '2px',
                      width: '100%',
                      marginLeft: 0,
                      marginRight: 0
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
        
        <Typography variant="body2" sx={{ 
          color: '#95a5a6', 
          marginTop: '16px',
          textAlign: 'center',
          fontStyle: 'italic',
          fontSize: '12px'
        }}>
          {t('task_footer_hint')}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ 
        backgroundColor: '#2c3e50', 
        padding: '14px 24px',
        justifyContent: 'space-between'
      }}>
        <Typography variant="body2" sx={{ 
          color: '#95a5a6',
          fontSize: '13px'
        }}>
          {t('task_selected_count', { count: selectedSoftware.length + selectedHardware.length })}
        </Typography>
        {/* 🔑 移除"取消"按钮，只保留"确认选择"，强制用户做出选择 */}
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          size="small"
          disabled={selectedSoftware.length === 0 && selectedHardware.length === 0}
          sx={{
            backgroundColor: '#27ae60',
            fontSize: '13px',
            '&:hover': { backgroundColor: '#219a52' },
            '&:disabled': { backgroundColor: '#7f8c8d' }
          }}
        >
          {t('task_confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskSelectionDialog; 